import { useCallback } from 'react'
import { encode } from 'js-base64'
import { AUTH_STEPS, VALIDATION_STATUS } from './constants'

/**
 * Hook to handle authentication submit logic
 */
export const useAuthSubmit = ({
	activeStep,
	formState,
	validationStatus,
	signMethod,
	verifyCode,
	password,
	authCode,
	deviceInfo,
	otpRefetch,
	verifyRefetch,
	_2faRefetch,
	verifyData,
	forgetPasswordOtpVerify,
	setToast,
	setValidationStatus,
	isLoading,
}) => {
	const handleSignupSubmit = useCallback(() => {
		if (!formState.rulesAccepted) {
			setToast({
				isError: true,
				show: true,
				message: 'register-rules-accept',
			})
			return
		}

		if (validationStatus === VALIDATION_STATUS.VALID && formState.rulesAccepted) {
			setValidationStatus(VALIDATION_STATUS.NORMAL)
			otpRefetch()
		}
	}, [formState.rulesAccepted, validationStatus, otpRefetch, setToast, setValidationStatus])

	const handleSigninSubmit = useCallback(() => {
		if (validationStatus === VALIDATION_STATUS.VALID) {
			setValidationStatus(VALIDATION_STATUS.NORMAL)
			otpRefetch()
		}
	}, [validationStatus, otpRefetch, setValidationStatus])

	const handleOtpSubmit = useCallback(() => {
		if (validationStatus !== VALIDATION_STATUS.VALID) return

		// Handle 2FA if required
		if (verifyData?.data?.data?.authenticator) {
			_2faRefetch({
				preToken: verifyData?.data?.data?.preToken,
				extra: deviceInfo,
				code: authCode,
			})
			return
		}

		// Regular OTP verification
		const payload = {
			[signMethod]: formState.mobileOrEmail,
			extra: deviceInfo,
			code: verifyCode,
			password: encode(password),
		}

		if (formState.affiliateCode) {
			payload.affiliateCode = formState.affiliateCode
		}

		verifyRefetch(payload)
	}, [
		validationStatus,
		verifyData,
		_2faRefetch,
		deviceInfo,
		authCode,
		signMethod,
		formState,
		verifyCode,
		password,
		verifyRefetch,
	])

	const handleForgetPasswordSubmit = useCallback(() => {
		if (validationStatus !== VALIDATION_STATUS.VALID) return

		const payload = {
			[signMethod]: formState.mobileOrEmail,
			extra: deviceInfo,
			code: verifyCode,
			password: encode(password),
		}

		forgetPasswordOtpVerify(payload)
	}, [validationStatus, signMethod, formState.mobileOrEmail, deviceInfo, verifyCode, password, forgetPasswordOtpVerify])

	const handleSubmit = useCallback(() => {
		if (isLoading) return

		switch (activeStep) {
			case AUTH_STEPS.SIGNUP:
				handleSignupSubmit()
				break
			case AUTH_STEPS.SIGNIN:
				handleSigninSubmit()
				break
			case AUTH_STEPS.SIGNUP_OTP:
			case AUTH_STEPS.SIGNIN_OTP:
				handleOtpSubmit()
				break
			case AUTH_STEPS.FORGET_PASSWORD:
				handleForgetPasswordSubmit()
				break
			default:
				break
		}
	}, [
		isLoading,
		activeStep,
		handleSignupSubmit,
		handleSigninSubmit,
		handleOtpSubmit,
		handleForgetPasswordSubmit,
	])

	return { handleSubmit }
}

